import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchProducts } from '../../store/slices/productsSlice';
import { GalleryService } from '../../services/galleryService';
import { FeaturedProductService } from '../../services/featuredProductService';
import GalleryCard from '../../components/Gallery/GalleryCard';
import { getPublicGalleryImageUrl } from '../../utils/galleryImageUtils';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Chip,
  Rating,
  useTheme,
  alpha,
  Fade,
  GlobalStyles,
} from '@mui/material';
// Import Google Fonts for premium typography
import '@fontsource/comfortaa/300.css';
import '@fontsource/comfortaa/400.css';
import '@fontsource/comfortaa/700.css';
import '@fontsource/quicksand/300.css';
import '@fontsource/quicksand/500.css';
import {
  Agriculture,
  LocalFlorist,
  Restaurant,
  Event,
  Hotel,
  ArrowForward,
  Verified,
} from '@mui/icons-material';
import FarmStayPromo from '../../components/Home/FarmStayPromo';
import { surface, brand, ink, line } from '../../theme/tokens';
import Seo, { farmJsonLd } from '../../components/Common/Seo';

// Convert signed URLs to public URLs - same logic as in ImageUpload component
const getPublicImageUrl = (url) => {
  // Handle null or undefined
  if (!url) {
    return '/placeholder-product.svg';
  }
  
  // Convert URL object to string if needed
  let urlString;
  if (typeof url === 'string') {
    urlString = url;
  } else if (url instanceof URL) {
    urlString = url.toString();
  } else if (typeof url === 'object' && url.href) {
    urlString = url.href;
  } else {
    console.warn('getPublicImageUrl received unsupported value:', url, typeof url);
    return '/placeholder-product.svg';
  }
  
  const bucketName = 'vidhifarm-storage-219dfeef04fa1-main';
  const region = 'ap-south-1';
  
  if (urlString.includes(`${bucketName}.s3.${region}.amazonaws.com/`)) {
    const urlParts = urlString.split(`${bucketName}.s3.${region}.amazonaws.com/`)[1];
    const filePath = urlParts ? urlParts.split('?')[0] : '';
    return `https://${bucketName}.s3.${region}.amazonaws.com/${filePath}`;
  }
  
  return urlString;
};

const HomePage = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { products, loading } = useSelector((state) => state.products);
  const [showText, setShowText] = useState(false);
  const [showSubtext, setShowSubtext] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const [galleries, setGalleries] = useState([]);
  const [galleryImages, setGalleryImages] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);

  useEffect(() => {
    // Fetch featured products from admin selection
    loadFeaturedProducts();
    
    // Fetch featured galleries
    loadGalleries();
    
    // Animate text appearance
    const textTimer = setTimeout(() => setShowText(true), 1000);
    const subtextTimer = setTimeout(() => setShowSubtext(true), 2000);
    
    return () => {
      clearTimeout(textTimer);
      clearTimeout(subtextTimer);
    };
  }, [dispatch]);

  const loadFeaturedProducts = async () => {
    try {
      const products = await FeaturedProductService.getFeaturedProductsForLanding();
      setFeaturedProducts(products);
    } catch (error) {
      console.error('Error loading featured products:', error);
      // Fallback to regular featured products if available
      dispatch(fetchProducts({ limit: 6, featured: true }));
    }
  };

  const loadGalleries = async () => {
    try {
      const response = await GalleryService.fetchPublicGalleries(6); // Get first 6 galleries
      setGalleries(response.items || []);
      
      // Collect all images from all galleries for the collage
      const allImages = [];
      for (const gallery of response.items || []) {
        try {
          const galleryDetail = await GalleryService.fetchGallery(gallery.id);
          if (galleryDetail.images && galleryDetail.images.items) {
            allImages.push(...galleryDetail.images.items);
          }
        } catch (error) {
          console.error(`Error loading images for gallery ${gallery.id}:`, error);
        }
      }
      
      // Sort by creation date and take first 3
      allImages.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setGalleryImages(allImages.slice(0, 3));
    } catch (error) {
      console.error('Error loading galleries:', error);
    }
  };

  // Use Redux products as fallback if no featured products from admin
  const fallbackProducts = products.slice(0, 6).map(product => {
    const primaryImage = product.images?.find(img => img.isPrimary);
    const fallbackImage = product.images?.[0];
    
    return {
      id: product.id,
      name: product.name,
      price: product.discountPrice || product.price,
      originalPrice: product.discountPrice ? product.price : null,
      unit: 'kg',
      image: getPublicImageUrl(primaryImage?.url || fallbackImage?.url) || '/placeholder-product.svg',
      rating: product.rating || 4.5,
      reviews: product.reviewCount || 0,
      isOrganic: product.farmingMethod === 'Organic',
      inStock: product.stock > 0,
      stock: product.stock,
      tags: product.tags || [],
      category: product.category?.name || '',
    };
  });

  // Use featured products from admin selection, fallback to regular products
  const displayProducts = featuredProducts.length > 0 ? featuredProducts : fallbackProducts;

  const farmExperiences = [
    {
      id: 1,
      title: 'Organic Farming Workshop',
      duration: '4 hours',
      price: 800,
      image: '/api/placeholder/300/200',
      rating: 4.7,
      participants: 15,
    },
    {
      id: 2,
      title: 'Farm to Table Cooking',
      duration: '3 hours',
      price: 1200,
      image: '/api/placeholder/300/200',
      rating: 4.9,
      participants: 12,
    },
    {
      id: 3,
      title: 'Sustainable Agriculture Tour',
      duration: '2 hours',
      price: 500,
      image: '/api/placeholder/300/200',
      rating: 4.6,
      participants: 20,
    },
  ];

  return (
    <Box>
      <Seo
        title="Organic Farm & Weekend Farm Stay in Birbhum"
        description="Vidhi Farm is an organic permaculture farm in Birbhum, West Bengal. Order farm-fresh produce, or book a 2-night weekend stay at ₹3,000 per person a night."
        path="/"
        jsonLd={farmJsonLd()}
      />

      <GlobalStyles
        styles={{
          '@keyframes pulse': {
            '0%': {
              transform: 'translate(-50%, -50%) scale(1)',
              opacity: 0.3,
            },
            '50%': {
              transform: 'translate(-50%, -50%) scale(1.1)',
              opacity: 0.1,
            },
            '100%': {
              transform: 'translate(-50%, -50%) scale(1)',
              opacity: 0.3,
            },
          },
        }}
      />
      {/* Hero Section with Background Video */}
      <Box
        sx={{
          position: 'relative',
          height: { xs: '60vh', md: '65vh', lg: '70vh' }, // Sleek, modern height
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          textAlign: 'center',
        }}
      >
        {/* YouTube Video Background */}
        <iframe
          src="https://www.youtube.com/embed/8FE7wNo1sb0?autoplay=1&mute=1&loop=1&playlist=8FE7wNo1sb0&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1"
          title="Vidhi Farm"
          frameBorder="0"
          allow="autoplay; encrypted-media"
          allowFullScreen
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '177.78vh', // 16:9 ratio coverage
            height: '56.25vw',
            minWidth: '100%',
            minHeight: '100%',
            objectFit: 'cover',
            zIndex: 0,
            pointerEvents: 'none',
            border: 'none',
          }}
          onError={() => {
            console.error('Video failed to load');
            setVideoError(true);
          }}
        />
        
        {/* Fallback gradient background if video fails */}
        {videoError && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              background: `linear-gradient(135deg, ${brand.olive} 0%, ${brand.brown} 100%)`,
              zIndex: 0,
            }}
          />
        )}
        
        {/* Premium Dark Overlay with gradient */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: `
              radial-gradient(
                ellipse at center,
                rgba(0, 0, 0, 0.2) 0%,
                rgba(0, 0, 0, 0.6) 100%
              )
            `,
            zIndex: 1,
          }}
        />
        
        {/* Additional gradient for the text area */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            // This scrim has to span the FULL hero, not the bottom 60%.
            // It holds a 0.35 alpha floor so the heading and CTA stay readable
            // whatever video plays -- but a floor inside a 60%-tall box means
            // alpha steps 0 -> 0.35 in one pixel row at the box's top edge,
            // drawing a hard line across the hero at the 40% mark. Most obvious
            // on mobile, where the short 60vh hero crops the video ~3.5x and
            // the step lands on smooth foliage with nothing to hide it.
            // Stops are remapped so the bottom 60% matches the old curve
            // exactly (0.45 now at 33%, 0.35 at 60%) and the floor then eases
            // to 0.22 at the top instead of falling off a cliff.
            background:
              'linear-gradient(to top, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0.45) 33%, rgba(0, 0, 0, 0.35) 60%, rgba(0, 0, 0, 0.22) 100%)',
            zIndex: 1,
          }}
        />

        {/* Content Overlay */}
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Fade in={showText} timeout={2000}>
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '3.5rem', md: '5rem', lg: '6rem' },
                fontWeight: 300, // Light weight for organic feel
                mb: 0,
                color: 'white',
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                textShadow: '0px 4px 30px rgba(0,0,0,0.7)',
                fontFamily: '"Comfortaa", "Quicksand", cursive', // Rounded, organic font
                background: `linear-gradient(180deg, ${surface.card} 0%, ${surface.page} 100%)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                lineHeight: 1.2,
              }}
            >
              Vidhi
            </Typography>
          </Fade>
          
          <Fade in={showSubtext} timeout={2000}>
            <Box sx={{ mb: 6 }}>
              <Box 
                sx={{ 
                  width: '60px', 
                  height: '1px', 
                  background: 'rgba(255,255,255,0.4)', 
                  margin: '15px auto',
                }}
              />
              <Typography
                variant="h4"
                sx={{
                  fontSize: { xs: '0.9rem', md: '1.1rem', lg: '1.2rem' },
                  fontWeight: 400,
                  color: 'rgba(255,255,255,0.9)',
                  letterSpacing: '0.35em',
                  textTransform: 'uppercase',
                  textShadow: '0px 2px 10px rgba(0,0,0,0.6)',
                  fontFamily: '"Quicksand", "Inter", sans-serif',
                }}
              >
                Permaculture Farm
              </Typography>
              <Box 
                sx={{ 
                  width: '60px', 
                  height: '1px', 
                  background: 'rgba(255,255,255,0.4)', 
                  margin: '15px auto 0',
                }}
              />
            </Box>
          </Fade>
          
          <Fade in={showSubtext} timeout={3000}>
            <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap', mt: 4 }}>
              <Button
                component={Link}
                to="/products"
                variant="outlined"
                size="large"
                sx={{
                  border: '1px solid rgba(255,255,255,0.3)',
                  color: 'white',
                  px: 4,
                  py: 1.2,
                  fontSize: '0.9rem',
                  letterSpacing: '0.2em',
                  textTransform: 'uppercase',
                  backdropFilter: 'blur(20px)',
                  background: 'rgba(255,255,255,0.05)',
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: '-100%',
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                    transition: 'left 0.5s',
                  },
                  '&:hover': {
                    border: '1px solid rgba(255,255,255,0.6)',
                    background: 'rgba(255,255,255,0.1)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
                    '&::before': {
                      left: '100%',
                    },
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                Explore Collection
              </Button>
            </Box>
          </Fade>
        </Container>
      </Box>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
        <Typography
          variant="h3"
          textAlign="center"
          sx={{
            mb: 2,
            fontSize: { xs: '1.8rem', sm: '2.5rem', md: '3rem' },
            fontWeight: 600,
            letterSpacing: '-0.015em',
            color: ink.primary,
          }}
        >
          Hand-Curated Just for Your Family
        </Typography>
        <Typography
          variant="body1"
          textAlign="center"
          sx={{
            mb: { xs: 5, md: 7 },
            fontSize: { xs: '1rem', md: '1.1rem' },
            lineHeight: 1.75,
            color: ink.secondary,
            maxWidth: 760,
            mx: 'auto',
            px: { xs: 2, sm: 0 }
          }}
        >
          Every single product you receive is personally selected, carefully inspected, and lovingly prepared by our family for yours.
          We know each plant by name, each harvest by heart, and each customer as part of our growing community.
        </Typography>

        <Grid container spacing={{ xs: 3, md: 4 }}>
          {[
            {
              icon: <LocalFlorist sx={{ fontSize: { xs: 36, md: 48 } }} />,
              title: 'Personally Nurtured',
              description: 'I walk through our fields every morning, checking each plant with my own hands. Every leaf, every fruit is grown with the same care I give my own family. No chemicals, no shortcuts - just patience, love, and traditional wisdom my grandmother taught me.',
            },
            {
              icon: <Verified sx={{ fontSize: { xs: 36, md: 48 } }} />,
              title: 'Hand-Selected Daily',
              description: 'At dawn, I personally choose each piece of produce that goes to your table. I test, I taste, I feel - only the best makes it into your basket. Your family deserves nothing less than what I\'d serve my own children.',
            },
            {
              icon: <Agriculture sx={{ fontSize: { xs: 36, md: 48 } }} />,
              title: 'Curated with Local Partners',
              description: 'Beyond our own farm, I collaborate with carefully chosen local artisans and small producers. Each honey, each processed item, each specialty product is personally vetted by me. I know the story behind every item in your order.',
            },
            {
              icon: <Restaurant sx={{ fontSize: { xs: 36, md: 48 } }} />,
              title: 'From My Heart to Yours',
              description: 'When you receive our products, you\'re not just getting food - you\'re getting a piece of our story, our passion, our commitment. I pack each order personally, imagining the smiles on your family\'s faces.',
            },
          ].map((feature, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card
                elevation={0}
                sx={{
                  textAlign: 'center',
                  p: { xs: 3, md: 3.5 },
                  height: '100%',
                  backgroundColor: surface.card,
                  border: `1px solid ${line.default}`,
                  borderRadius: 3,
                  boxShadow: '0 1px 2px rgba(75,58,55,0.04)',
                  '&:hover': {
                    transform: { xs: 'none', md: 'translateY(-4px)' },
                    borderColor: line.strong,
                    boxShadow: { xs: 'none', md: '0 16px 32px rgba(75,58,55,0.12)' },
                  },
                  transition: 'all 0.3s ease-in-out',
                }}
              >
                <Box
                  sx={{
                    color: brand.brown,
                    mb: 2.5,
                    mx: 'auto',
                    width: { xs: 64, md: 76 },
                    height: { xs: 64, md: 76 },
                    borderRadius: '50%',
                    backgroundColor: surface.sunken,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {feature.icon}
                </Box>
                <Typography
                  variant="h6"
                  sx={{ mb: 1.5, fontWeight: 700, color: ink.primary, letterSpacing: '-0.01em' }}
                >
                  {feature.title}
                </Typography>
                <Typography variant="body2" sx={{ color: ink.secondary, lineHeight: 1.7 }}>
                  {feature.description}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      <FarmStayPromo />

      {/* Featured Products Section */}
      <Box sx={{ backgroundColor: surface.sunken, py: { xs: 6, md: 10 } }}>
        <Container maxWidth="lg">
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'center', sm: 'center' },
            mb: { xs: 5, md: 7 },
            gap: { xs: 2, sm: 0 }
          }}>
            <Box>
              <Typography
                variant="h3"
                sx={{
                  fontSize: { xs: '1.8rem', sm: '2.5rem', md: '3rem' },
                  textAlign: { xs: 'center', sm: 'left' },
                  fontWeight: 600,
                  letterSpacing: '-0.015em',
                  color: ink.primary,
                  mb: 1.5
                }}
              >
                My Personal Selections for You
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { xs: '0.95rem', md: '1rem' },
                  lineHeight: 1.75,
                  color: ink.secondary,
                  textAlign: { xs: 'center', sm: 'left' },
                  maxWidth: 520
                }}
              >
                Each item here is something I\'m personally proud of - products I grow myself or source from trusted neighbors. These aren\'t just ingredients; they\'re the fruits of relationships, care, and countless conversations with local artisans who share my values.
              </Typography>
            </Box>
            <Button
              component={Link}
              to="/products"
              variant="outlined"
              endIcon={<ArrowForward />}
              sx={{ 
                minWidth: { xs: '200px', sm: 'auto' },
                fontSize: { xs: '0.9rem', sm: '1rem' }
              }}
            >
              View All Products
            </Button>
          </Box>
          
          <Grid container spacing={{ xs: 3, md: 4 }}>
            {displayProducts.map((product) => (
              <Grid item xs={12} sm={6} md={4} key={product.id}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    backgroundColor: surface.card,
                    border: `1px solid ${line.subtle}`,
                    borderRadius: 3,
                    overflow: 'hidden',
                    transition: 'transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease',
                    '&:hover': {
                      transform: { xs: 'none', md: 'translateY(-4px)' },
                      borderColor: line.default,
                      boxShadow: { xs: 'none', md: '0 18px 36px rgba(75,58,55,0.14)' },
                    },
                  }}
                >
                  <CardMedia
                    component="img"
                    height="200"
                    image={product.image}
                    alt={product.name}
                    sx={{ objectFit: 'cover', backgroundColor: surface.sunken }}
                  />
                  <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
                    <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                      {product.isOrganic && (
                        <Chip
                          icon={<Verified />}
                          label="Organic"
                          color="success"
                          size="small"
                        />
                      )}
                      <Chip
                        label={product.inStock ? 'In Stock' : 'Out of Stock'}
                        color={product.inStock ? 'success' : 'error'}
                        variant="outlined"
                        size="small"
                      />
                    </Box>
                    
                    <Typography
                      variant="h6"
                      sx={{ mb: 1, fontWeight: 700, color: ink.primary, letterSpacing: '-0.01em' }}
                    >
                      {product.name}
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Rating value={product.rating} precision={0.1} size="small" readOnly />
                      <Typography variant="body2" sx={{ ml: 1, color: ink.muted }}>
                        ({product.reviews} reviews)
                      </Typography>
                    </Box>

                    <Typography variant="h5" sx={{ fontWeight: 700, color: brand.mustardInk }}>
                      ₹{product.price}
                      <Typography component="span" variant="body2" sx={{ ml: 0.25, color: ink.muted }}>
                        /{product.unit}
                      </Typography>
                    </Typography>
                  </CardContent>

                  <CardActions sx={{ p: 2.5, pt: 0 }}>
                    <Button
                      component={Link}
                      to={`/products/${product.id}`}
                      variant="outlined"
                      fullWidth
                      disabled={!product.inStock}
                    >
                      {product.inStock ? 'View Details' : 'Notify When Available'}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Farm Gallery Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
        <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 7 } }}>
          <Typography
            variant="h3"
            sx={{
              mb: 2,
              fontSize: { xs: '1.8rem', sm: '2.5rem', md: '3rem' },
              fontWeight: 600,
              letterSpacing: '-0.015em',
              color: ink.primary,
            }}
          >
            Behind the Scenes of Your Food
          </Typography>
          <Typography
            variant="body1"
            sx={{
              maxWidth: 700,
              mx: 'auto',
              px: { xs: 2, sm: 0 },
              fontSize: { xs: '0.95rem', md: '1rem' },
              lineHeight: 1.75,
              color: ink.secondary,
            }}
          >
            These photos aren\'t just pretty pictures - they\'re glimpses into the daily decisions I make for your family. 
            See the hands that tend your vegetables, the morning rituals that ensure quality, the faces of local partners 
            I trust. This is transparency at its most personal - welcome to my world.
          </Typography>
        </Box>
        
        {galleryImages.length >= 3 ? (
          <>
            {/* Gallery Collage Card */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
              <Card 
                sx={{ 
                  maxWidth: { xs: '100%', sm: 600 }, 
                  width: '100%',
                  mx: { xs: 2, sm: 0 },
                  overflow: 'hidden',
                  borderRadius: 3,
                  boxShadow: theme.shadows[8],
                  cursor: 'pointer',
                  transition: 'transform 0.2s ease-in-out',
                  '&:hover': {
                    transform: { xs: 'none', md: 'translateY(-4px)' },
                    boxShadow: { xs: theme.shadows[8], md: theme.shadows[16] }
                  }
                }}
                onClick={() => window.location.href = '/gallery'}
              >
                <Box sx={{ position: 'relative', height: { xs: 250, sm: 300, md: 400 } }}>
                  {/* Main image (first image) */}
                  <Box
                    sx={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      width: '70%',
                      height: '100%',
                      backgroundImage: `url(${getPublicGalleryImageUrl(galleryImages[0]?.thumbnailUrl)})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat'
                    }}
                  />
                  
                  {/* Second image (top right) */}
                  <Box
                    sx={{
                      position: 'absolute',
                      right: 0,
                      top: 0,
                      width: '30%',
                      height: '50%',
                      backgroundImage: `url(${getPublicGalleryImageUrl(galleryImages[1]?.thumbnailUrl)})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat',
                      borderLeft: `2px solid ${surface.card}`
                    }}
                  />
                  
                  {/* Third image (bottom right) */}
                  <Box
                    sx={{
                      position: 'absolute',
                      right: 0,
                      bottom: 0,
                      width: '30%',
                      height: '50%',
                      backgroundImage: `url(${getPublicGalleryImageUrl(galleryImages[2]?.thumbnailUrl)})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat',
                      borderLeft: `2px solid ${surface.card}`,
                      borderTop: `2px solid ${surface.card}`
                    }}
                  />
                  
                  {/* Overlay with gallery count */}
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)',
                      color: 'white',
                      p: 3
                    }}
                  >
                    <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                      Farm Gallery
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      {galleryImages.length} beautiful moments captured
                    </Typography>
                  </Box>
                </Box>
              </Card>
            </Box>
            
            <Box sx={{ textAlign: 'center', mt: 4 }}>
              <Button
                component={Link}
                to="/gallery"
                variant="outlined"
                size="large"
                endIcon={<ArrowForward />}
              >
                View All Galleries
              </Button>
            </Box>
          </>
        ) : galleryImages.length > 0 ? (
          <>
            {/* Fallback for less than 3 images */}
            <Grid container spacing={3}>
              {galleries.slice(0, 6).map((gallery) => (
                <Grid item xs={12} sm={6} md={4} key={gallery.id}>
                  <GalleryCard
                    gallery={gallery}
                    onClick={() => window.location.href = `/gallery/${gallery.id}`}
                    variant="compact"
                    showImageCount={true}
                    showDate={false}
                    showLocation={false}
                  />
                </Grid>
              ))}
            </Grid>
            
            <Box sx={{ textAlign: 'center', mt: 4 }}>
              <Button
                component={Link}
                to="/gallery"
                variant="outlined"
                size="large"
                endIcon={<ArrowForward />}
              >
                View All Galleries
              </Button>
            </Box>
          </>
        ) : (
          <Grid container spacing={3}>
            {[1, 2, 3, 4, 5, 6].map((index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Box
                  sx={{
                    position: 'relative',
                    paddingTop: '75%', // 4:3 Aspect Ratio
                    borderRadius: 2,
                    overflow: 'hidden',
                    border: `1px solid ${line.subtle}`,
                    bgcolor: surface.sunken,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Typography variant="body2" sx={{ color: ink.secondary }}>
                    Gallery {index}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>

      {/* Farm Story Section */}
      <Box sx={{ backgroundColor: surface.dark, color: ink.onDark, py: { xs: 6, md: 10 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography 
                variant="h3" 
                sx={{ 
                  mb: { xs: 3, md: 4 },
                  color: ink.onDark,
                  fontWeight: 600,
                  letterSpacing: '-0.015em',
                  fontSize: { xs: '1.8rem', sm: '2.5rem', md: '3rem' },
                  textAlign: { xs: 'center', md: 'left' }
                }}
              >
                A Personal Journey, Shared with You
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  mb: 3, 
                  fontSize: { xs: '1rem', md: '1.1rem' }, 
                  lineHeight: 1.8,
                  color: ink.onDarkMuted,
                  textAlign: { xs: 'center', md: 'left' }
                }}
              >
                When I started Vidhi Farm, it wasn\'t about business - it was about feeding my own family the way my grandmother fed hers. 
                I wanted my children to taste real tomatoes, to know where their food comes from, to understand that good things take time and care. 
                What began as a personal mission has become my life's calling: to be your trusted food curator.
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  mb: 4, 
                  fontSize: { xs: '1rem', md: '1.1rem' }, 
                  lineHeight: 1.8,
                  color: ink.onDarkMuted,
                  textAlign: { xs: 'center', md: 'left' }
                }}
              >
                On our 10-bigha farm, I don\'t just grow crops - I cultivate relationships. With my moringa trees that I planted as saplings, 
                with my heritage banana varieties that tell stories of old times, with local honey producers who've become like family. 
                When you buy from us, you're not just purchasing food; you're joining a circle of trust that spans generations.
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  mb: 4, 
                  fontSize: { xs: '1rem', md: '1.1rem' }, 
                  lineHeight: 1.8,
                  color: ink.onDarkMuted,
                  textAlign: { xs: 'center', md: 'left' }
                }}
              >
                Every morning, I make decisions with your family in mind. Which fruits are perfect today? Which vegetables would make your children smile? 
                Which artisan\'s honey captures the essence of our local flowers? This isn\'t farming - it\'s curation with heart, connection, and complete transparency.
              </Typography>
              <Button
                component={Link}
                to="/about"
                variant="outlined"
                sx={{
                  borderColor: ink.onDark,
                  color: ink.onDark,
                  '&:hover': {
                    borderColor: ink.onDark,
                    backgroundColor: alpha(ink.onDark, 0.12),
                  },
                }}
                endIcon={<ArrowForward />}
              >
                {/* /about is the year-by-year journey now, not a generic
                    about page, so the label should say what it actually is. */}
                See how the farm grew
              </Button>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  position: 'relative',
                  height: { xs: '300px', sm: '400px', md: '600px' },
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  perspective: { xs: '800px', md: '1200px' },
                  overflow: 'visible',
                  mx: { xs: 2, sm: 3, md: 4 },
                  px: { xs: 2, sm: 4, md: 6 },
                }}
              >
                {/* Floating Video Stack */}
                {[1, 2, 3, 4, 5].map((index) => (
                  <Box
                    key={index}
                    sx={{
                      position: 'absolute',
                      width: { xs: '100px', sm: '130px', md: '180px' },
                      height: { xs: '178px', sm: '231px', md: '320px' }, // 9:16 aspect ratio
                      borderRadius: { xs: '12px', md: '16px' },
                      overflow: 'hidden',
                      boxShadow: { xs: '0 10px 30px rgba(0,0,0,0.4)', md: '0 20px 50px rgba(0,0,0,0.4)' },
                      border: '3px solid rgba(255,255,255,0.2)',
                      transform: {
                        xs: `
                          translateX(${(index - 3) * 45}px) 
                          translateY(${
                            index === 1 ? -30 : 
                            index === 2 ? -15 : 
                            index === 3 ? 0 : 
                            index === 4 ? 15 : 
                            30
                          }px) 
                          rotateY(${(index - 3) * 5}deg)
                          scale(${1 - Math.abs(index - 3) * 0.1})
                        `,
                        sm: `
                          translateX(${(index - 3) * 60}px) 
                          translateY(${
                            index === 1 ? -40 : 
                            index === 2 ? -20 : 
                            index === 3 ? 0 : 
                            index === 4 ? 20 : 
                            40
                          }px) 
                          rotateY(${(index - 3) * 6}deg) 
                          scale(${1 - Math.abs(index - 3) * 0.08})
                        `,
                        md: `
                          translateX(${(index - 3) * 90}px) 
                          translateY(${
                            index === 1 ? -60 : 
                            index === 2 ? -30 : 
                            index === 3 ? 0 : 
                            index === 4 ? 30 : 
                            60
                          }px) 
                          rotateY(${(index - 3) * 7}deg) 
                          rotateX(2deg)
                          rotateZ(${(index - 3) * 2}deg)
                          scale(${1 - Math.abs(index - 3) * 0.06})
                        `
                      },
                      zIndex: 6 - Math.abs(index - 3),
                      transition: 'all 0.4s ease',
                      '&:hover': {
                        transform: {
                          xs: `
                            translateX(${(index - 3) * 50}px) 
                            translateY(${
                              index === 1 ? -35 : 
                              index === 2 ? -18 : 
                              index === 3 ? -5 : 
                              index === 4 ? 18 : 
                              35
                            }px) 
                            rotateY(${(index - 3) * 6}deg)
                            scale(${1.05 - Math.abs(index - 3) * 0.1})
                          `,
                          md: `
                            translateX(${(index - 3) * 100}px) 
                            translateY(${
                              index === 1 ? -70 : 
                              index === 2 ? -35 : 
                              index === 3 ? -10 : 
                              index === 4 ? 35 : 
                              70
                            }px) 
                            rotateY(${(index - 3) * 9}deg) 
                            rotateX(0deg)
                            rotateZ(${(index - 3) * 3}deg)
                            scale(${1.1 - Math.abs(index - 3) * 0.06})
                          `
                        },
                        boxShadow: { xs: '0 20px 50px rgba(0,0,0,0.5)', md: '0 35px 80px rgba(0,0,0,0.6)' },
                      },
                    }}
                  >
                    <video
                      autoPlay
                      loop
                      muted
                      playsInline
                      preload="metadata"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                      onError={(e) => {
                        console.error(`Video ${index} failed to load:`, e);
                        // Show fallback placeholder safely
                        if (e.target) {
                          e.target.style.display = 'none';
                          if (e.target.nextSibling) {
                            e.target.nextSibling.style.display = 'flex';
                          }
                        }
                      }}
                    >
                      <source src={`/assets/My Movie_${index}.mp4`} type="video/mp4" />
                    </video>
                    
                    {/* Fallback placeholder */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        display: 'none',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: `linear-gradient(135deg, ${brand.olive} 0%, ${brand.brown} 100%)`,
                        color: ink.onDark,
                        fontSize: '0.8rem',
                        textAlign: 'center',
                        padding: '8px',
                      }}
                    >
                      Farm Video {index}
                    </Box>
                    
                    {/* Subtle overlay for premium look */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(0,0,0,0.1) 100%)',
                        pointerEvents: 'none',
                      }}
                    />
                  </Box>
                ))}
                
                {/* Ambient glow effect */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '200px', sm: '250px', md: '300px' },
                    height: { xs: '200px', sm: '250px', md: '300px' },
                    background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
                    borderRadius: '50%',
                    zIndex: -1,
                    animation: 'pulse 4s ease-in-out infinite',
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Call to Action Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 }, textAlign: 'center', px: { xs: 2, sm: 3 } }}>
        <Typography
          variant="h3"
          sx={{
            mb: 2,
            fontSize: { xs: '1.8rem', sm: '2.5rem', md: '3rem' },
            fontWeight: 600,
            letterSpacing: '-0.015em',
            color: ink.primary,
          }}
        >
          Let Me Personally Serve Your Family
        </Typography>
        <Typography
          variant="body1"
          sx={{
            mb: 2,
            fontSize: { xs: '1.1rem', md: '1.3rem' },
            fontWeight: 600,
            maxWidth: 700,
            mx: 'auto',
            px: { xs: 2, sm: 0 },
            color: brand.mustardInk
          }}
        >
          Trust me to curate nature's finest treasures for your table
        </Typography>
        <Typography 
          variant="body1" 
          sx={{ 
            mb: { xs: 4, md: 6 }, 
            fontSize: { xs: '1rem', md: '1.1rem' }, 
            maxWidth: 650, 
            mx: 'auto',
            px: { xs: 2, sm: 0 },
            lineHeight: 1.75,
            color: ink.secondary
          }}
        >
          When you choose Vidhi Farm, you\'re not just buying groceries - you\'re inviting me to be your personal food curator. 
          I'll handpick each item with the same care I show my own family, building a relationship that goes beyond commerce. 
          Let's create something beautiful together: a connection to real food, real stories, and real care.
        </Typography>
        
        <Box sx={{ 
          display: 'flex', 
          gap: { xs: 2, md: 3 }, 
          justifyContent: 'center', 
          flexWrap: 'wrap',
          px: { xs: 2, sm: 0 }
        }}>
          <Button
            component={Link}
            to="/contact"
            variant="contained"
            size="large"
            sx={{ 
              px: { xs: 3, md: 4 }, 
              py: { xs: 1.2, md: 1.5 },
              fontSize: { xs: '0.9rem', md: '1rem' },
              minWidth: { xs: '160px', sm: 'auto' }
            }}
          >
            Start Our Journey Together
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default HomePage;